import { StyleSheet } from "@react-pdf/renderer";
import { type CVTemplateId, type CVFontFamily } from "./cv.types";

export interface StyleConfig {
  template: CVTemplateId;
  fontFamily: CVFontFamily;
  accentColor: string;
  density?: "standard" | "compact";
}

export function createCVStyles({
  template,
  fontFamily,
  accentColor,
  density = "standard",
}: StyleConfig) {
  const isTimes = fontFamily === "times";
  const regularFont = isTimes ? "Times-Roman" : "Helvetica";
  const boldFont = isTimes ? "Times-Bold" : "Helvetica-Bold";
  const italicFont = isTimes ? "Times-Italic" : "Helvetica-Oblique";

  const isCompact = density === "compact";
  const pagePaddingTop = isCompact ? 28 : 36;
  const pagePaddingBottom = isCompact ? 32 : 40;
  const sectionMarginBottom = isCompact ? 10 : 14;
  const itemMarginBottom = isCompact ? 6 : 8;

  // Shared baseline
  const basePage = {
    paddingTop: pagePaddingTop,
    paddingBottom: pagePaddingBottom,
    paddingHorizontal: 40,
    fontFamily: regularFont,
    fontSize: 9.5,
    lineHeight: 1.4,
    color: "#1e293b",
  };

  if (template === "modern-executive") {
    return StyleSheet.create({
      page: basePage,
      headerContainer: {
        marginBottom: 16,
        paddingBottom: 12,
        borderBottomWidth: 2,
        borderBottomColor: accentColor,
      },
      nameWrapper: {
        width: "100%",
        marginBottom: 3,
      },
      name: {
        fontSize: 22,
        fontFamily: boldFont,
        lineHeight: 1.2,
        color: accentColor,
        letterSpacing: -0.2,
      },
      titleWrapper: {
        width: "100%",
        marginBottom: 6,
      },
      title: {
        fontSize: 11,
        fontFamily: regularFont,
        lineHeight: 1.3,
        color: "#475569",
      },
      contactRow: {
        flexDirection: "row",
        flexWrap: "wrap",
        marginTop: 4,
        width: "100%",
      },
      contactItem: {
        flexDirection: "row",
        alignItems: "center",
        marginRight: 10,
        marginBottom: 3,
        fontSize: 8.5,
        color: "#64748b",
      },
      contactSeparator: {
        marginRight: 10,
        color: "#cbd5e1",
      },
      link: {
        color: accentColor,
        textDecoration: "none",
      },
      section: {
        marginBottom: sectionMarginBottom,
      },
      sectionHeaderContainer: {
        flexDirection: "row",
        alignItems: "center",
        marginBottom: 8,
        paddingBottom: 3,
        borderBottomWidth: 1,
        borderBottomColor: "#e2e8f0",
      },
      sectionTitle: {
        fontSize: 10.5,
        fontFamily: boldFont,
        textTransform: "uppercase",
        letterSpacing: 0.8,
        color: accentColor,
        borderLeftWidth: 3,
        borderLeftColor: accentColor,
        paddingLeft: 6,
      },
      itemGroup: {
        marginBottom: itemMarginBottom,
      },
      itemHeaderRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "baseline",
        marginBottom: 2,
      },
      itemTitle: {
        fontFamily: boldFont,
        fontSize: 10,
        color: "#0f172a",
      },
      itemDate: {
        fontSize: 8.5,
        color: "#64748b",
        fontFamily: regularFont,
        flexShrink: 0,
        marginLeft: 8,
      },
      itemSubtitleRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "baseline",
        marginBottom: 2,
      },
      itemSubtitle: {
        fontFamily: boldFont,
        fontSize: 9,
        color: "#334155",
      },
      itemLocation: {
        fontSize: 8.5,
        color: "#64748b",
        fontFamily: italicFont,
        flexShrink: 0,
        marginLeft: 8,
      },
      paragraph: {
        fontSize: 9,
        lineHeight: 1.45,
        color: "#334155",
        textAlign: "justify",
      },
      badge: {
        fontSize: 7.5,
        fontFamily: boldFont,
        color: accentColor,
        backgroundColor: "#f1f5f9",
        paddingHorizontal: 4,
        paddingVertical: 1.5,
        borderRadius: 2,
        marginRight: 4,
      },
      pubItem: {
        marginBottom: itemMarginBottom,
      },
      pubHeaderRow: {
        flexDirection: "row",
        alignItems: "flex-start",
      },
      pubNumber: {
        fontFamily: boldFont,
        fontSize: 8.5,
        color: accentColor,
        width: 22,
        marginRight: 4,
        flexShrink: 0,
      },
      pubContent: {
        flex: 1,
      },
      citationText: {
        fontSize: 9,
        lineHeight: 1.4,
        color: "#1e293b",
        marginBottom: 2,
      },
      researchAreaItem: {
        marginBottom: 6,
      },
      researchAreaTitle: {
        fontFamily: boldFont,
        fontSize: 9.5,
        color: accentColor,
        marginBottom: 2,
      },
      researchAreaDescription: {
        fontFamily: regularFont,
        fontSize: 9,
        lineHeight: 1.4,
        color: "#334155",
        marginBottom: 2,
      },
      researchAreaKeywords: {
        fontFamily: italicFont,
        fontSize: 8,
        color: "#64748b",
      },
      footer: {
        position: "absolute",
        bottom: 20,
        left: 40,
        right: 40,
        flexDirection: "row",
        justifyContent: "space-between",
        fontSize: 8,
        color: "#94a3b8",
        borderTopWidth: 0.5,
        borderTopColor: "#e2e8f0",
        paddingTop: 6,
      },
    });
  }

  if (template === "minimal-clean") {
    return StyleSheet.create({
      page: {
        ...basePage,
        paddingTop: pagePaddingTop + 4,
        paddingBottom: pagePaddingBottom + 2,
        paddingHorizontal: 44,
      },
      headerContainer: {
        marginBottom: 16,
        paddingBottom: 10,
      },
      nameWrapper: {
        width: "100%",
        marginBottom: 3,
      },
      name: {
        fontSize: 20,
        fontFamily: boldFont,
        lineHeight: 1.2,
        color: "#0f172a",
        letterSpacing: 0.5,
      },
      titleWrapper: {
        width: "100%",
        marginBottom: 6,
      },
      title: {
        fontSize: 10.5,
        fontFamily: regularFont,
        lineHeight: 1.3,
        color: "#64748b",
      },
      contactRow: {
        flexDirection: "row",
        flexWrap: "wrap",
        marginTop: 4,
        width: "100%",
      },
      contactItem: {
        flexDirection: "row",
        alignItems: "center",
        marginRight: 8,
        marginBottom: 3,
        fontSize: 8,
        color: "#64748b",
      },
      contactSeparator: {
        marginRight: 8,
        color: "#cbd5e1",
      },
      link: {
        color: "#0f172a",
        textDecoration: "underline",
      },
      section: {
        marginBottom: sectionMarginBottom,
      },
      sectionHeaderContainer: {
        marginBottom: 7,
        borderBottomWidth: 0.5,
        borderBottomColor: "#cbd5e1",
        paddingBottom: 2,
      },
      sectionTitle: {
        fontSize: 9.5,
        fontFamily: boldFont,
        textTransform: "uppercase",
        letterSpacing: 1.2,
        color: "#475569",
      },
      itemGroup: {
        marginBottom: itemMarginBottom,
      },
      itemHeaderRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "baseline",
        marginBottom: 1,
      },
      itemTitle: {
        fontFamily: boldFont,
        fontSize: 9.5,
        color: "#0f172a",
      },
      itemDate: {
        fontSize: 8,
        color: "#94a3b8",
        flexShrink: 0,
        marginLeft: 8,
      },
      itemSubtitleRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        marginBottom: 2,
      },
      itemSubtitle: {
        fontFamily: regularFont,
        fontSize: 9,
        color: "#475569",
      },
      itemLocation: {
        fontSize: 8,
        color: "#94a3b8",
        flexShrink: 0,
        marginLeft: 8,
      },
      paragraph: {
        fontSize: 8.5,
        lineHeight: 1.4,
        color: "#334155",
      },
      badge: {
        fontSize: 7,
        fontFamily: regularFont,
        color: "#64748b",
        borderWidth: 0.5,
        borderColor: "#cbd5e1",
        paddingHorizontal: 3,
        paddingVertical: 1,
        marginRight: 4,
      },
      pubItem: {
        marginBottom: itemMarginBottom,
      },
      pubHeaderRow: {
        flexDirection: "row",
        alignItems: "flex-start",
      },
      pubNumber: {
        fontFamily: boldFont,
        fontSize: 8,
        color: "#475569",
        width: 20,
        marginRight: 3,
        flexShrink: 0,
      },
      pubContent: {
        flex: 1,
      },
      citationText: {
        fontSize: 8.5,
        lineHeight: 1.35,
        color: "#1e293b",
        marginBottom: 2,
      },
      researchAreaItem: {
        marginBottom: 5,
      },
      researchAreaTitle: {
        fontFamily: boldFont,
        fontSize: 9,
        color: "#0f172a",
        marginBottom: 1,
      },
      researchAreaDescription: {
        fontFamily: regularFont,
        fontSize: 8.5,
        lineHeight: 1.35,
        color: "#334155",
        marginBottom: 1,
      },
      researchAreaKeywords: {
        fontFamily: italicFont,
        fontSize: 7.5,
        color: "#94a3b8",
      },
      footer: {
        position: "absolute",
        bottom: 20,
        left: 44,
        right: 44,
        flexDirection: "row",
        justifyContent: "space-between",
        fontSize: 7.5,
        color: "#94a3b8",
      },
    });
  }

  if (template === "compact-two-column") {
    return StyleSheet.create({
      page: {
        ...basePage,
        paddingTop: pagePaddingTop - 4,
        paddingBottom: pagePaddingBottom - 4,
        paddingHorizontal: 32,
      },
      headerContainer: {
        marginBottom: 12,
        paddingBottom: 8,
        borderBottomWidth: 1.5,
        borderBottomColor: accentColor,
      },
      nameWrapper: {
        width: "100%",
        marginBottom: 2,
      },
      name: {
        fontSize: 22,
        fontFamily: boldFont,
        lineHeight: 1.2,
        color: accentColor,
      },
      titleWrapper: {
        width: "100%",
        marginBottom: 4,
      },
      title: {
        fontSize: 10.5,
        fontFamily: regularFont,
        lineHeight: 1.3,
        color: "#475569",
      },
      contactRow: {
        flexDirection: "column",
        marginTop: 4,
        width: "100%",
      },
      contactItem: {
        flexDirection: "row",
        alignItems: "center",
        marginBottom: 3,
        fontSize: 8,
        color: "#475569",
      },
      contactSeparator: {
        display: "none",
      },
      link: {
        color: accentColor,
        textDecoration: "none",
      },
      columnsWrapper: {
        flexDirection: "row",
        gap: 16,
      },
      sidebarColumn: {
        width: "32%",
      },
      mainColumn: {
        width: "68%",
      },
      section: {
        marginBottom: sectionMarginBottom - 2,
      },
      sectionHeaderContainer: {
        marginBottom: 5,
        paddingBottom: 2,
        borderBottomWidth: 1,
        borderBottomColor: accentColor,
      },
      sectionTitle: {
        fontSize: 9.5,
        fontFamily: boldFont,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        color: accentColor,
      },
      itemGroup: {
        marginBottom: itemMarginBottom - 1,
      },
      itemHeaderRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "baseline",
        marginBottom: 1,
      },
      itemTitle: {
        fontFamily: boldFont,
        fontSize: 9.5,
        color: "#0f172a",
      },
      itemDate: {
        fontSize: 8,
        color: "#64748b",
        flexShrink: 0,
        marginLeft: 6,
      },
      itemSubtitleRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        marginBottom: 2,
      },
      itemSubtitle: {
        fontFamily: italicFont,
        fontSize: 8.5,
        color: "#334155",
      },
      itemLocation: {
        fontSize: 8,
        color: "#64748b",
        flexShrink: 0,
        marginLeft: 6,
      },
      paragraph: {
        fontSize: 8.5,
        lineHeight: 1.35,
        color: "#334155",
      },
      badge: {
        fontSize: 7,
        fontFamily: boldFont,
        color: accentColor,
        backgroundColor: "#f1f5f9",
        paddingHorizontal: 3,
        paddingVertical: 1,
        marginRight: 3,
      },
      pubItem: {
        marginBottom: itemMarginBottom - 1,
      },
      pubHeaderRow: {
        flexDirection: "row",
        alignItems: "flex-start",
      },
      pubNumber: {
        fontFamily: boldFont,
        fontSize: 8,
        color: accentColor,
        width: 18,
        marginRight: 3,
        flexShrink: 0,
      },
      pubContent: {
        flex: 1,
      },
      citationText: {
        fontSize: 8.5,
        lineHeight: 1.35,
        color: "#1e293b",
        marginBottom: 2,
      },
      researchAreaItem: {
        marginBottom: 5,
      },
      researchAreaTitle: {
        fontFamily: boldFont,
        fontSize: 9,
        color: accentColor,
        marginBottom: 1,
      },
      researchAreaDescription: {
        fontFamily: regularFont,
        fontSize: 8.5,
        lineHeight: 1.35,
        color: "#334155",
        marginBottom: 1,
      },
      researchAreaKeywords: {
        fontFamily: italicFont,
        fontSize: 7.5,
        color: "#64748b",
      },
      footer: {
        position: "absolute",
        bottom: 18,
        left: 32,
        right: 32,
        flexDirection: "row",
        justifyContent: "space-between",
        fontSize: 7.5,
        color: "#94a3b8",
        borderTopWidth: 0.5,
        borderTopColor: "#e2e8f0",
        paddingTop: 4,
      },
    });
  }

  // Default: academic-classic (Harvard / Oxford Ivy Traditional)
  return StyleSheet.create({
    page: basePage,
    headerContainer: {
      marginBottom: 16,
      alignItems: "center",
      textAlign: "center",
      borderBottomWidth: 1.5,
      borderBottomColor: "#1e293b",
      paddingBottom: 10,
    },
    nameWrapper: {
      width: "100%",
      alignItems: "center",
      marginBottom: 3,
    },
    name: {
      fontSize: 22,
      fontFamily: boldFont,
      lineHeight: 1.2,
      letterSpacing: 1.2,
      textTransform: "uppercase",
      color: "#0f172a",
      textAlign: "center",
    },
    titleWrapper: {
      width: "100%",
      alignItems: "center",
      marginBottom: 6,
    },
    title: {
      fontSize: 10.5,
      fontFamily: italicFont,
      lineHeight: 1.3,
      color: "#334155",
      textAlign: "center",
    },
    contactRow: {
      flexDirection: "row",
      flexWrap: "wrap",
      justifyContent: "center",
      marginTop: 4,
      width: "100%",
    },
    contactItem: {
      flexDirection: "row",
      alignItems: "center",
      marginHorizontal: 4,
      marginBottom: 3,
      fontSize: 8.5,
      color: "#475569",
    },
    contactSeparator: {
      marginRight: 6,
      color: "#94a3b8",
    },
    link: {
      color: accentColor,
      textDecoration: "none",
    },
    section: {
      marginBottom: sectionMarginBottom,
    },
    sectionHeaderContainer: {
      marginBottom: 8,
      borderBottomWidth: 0.75,
      borderBottomColor: "#334155",
      paddingBottom: 2,
    },
    sectionTitle: {
      fontSize: 10.5,
      fontFamily: boldFont,
      textTransform: "uppercase",
      letterSpacing: 1.2,
      color: "#0f172a",
    },
    itemGroup: {
      marginBottom: itemMarginBottom,
    },
    itemHeaderRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "baseline",
      marginBottom: 1,
    },
    itemTitle: {
      fontFamily: boldFont,
      fontSize: 10,
      color: "#0f172a",
    },
    itemDate: {
      fontSize: 8.5,
      color: "#475569",
      fontFamily: regularFont,
      flexShrink: 0,
      marginLeft: 8,
    },
    itemSubtitleRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "baseline",
      marginBottom: 2,
    },
    itemSubtitle: {
      fontFamily: italicFont,
      fontSize: 9.5,
      color: "#334155",
    },
    itemLocation: {
      fontSize: 8.5,
      color: "#475569",
      fontFamily: regularFont,
      flexShrink: 0,
      marginLeft: 8,
    },
    paragraph: {
      fontSize: 9,
      lineHeight: 1.45,
      color: "#1e293b",
      textAlign: "justify",
    },
    badge: {
      fontSize: 7.5,
      fontFamily: boldFont,
      color: "#475569",
      borderWidth: 0.5,
      borderColor: "#94a3b8",
      paddingHorizontal: 3,
      paddingVertical: 1,
      marginRight: 4,
    },
    pubItem: {
      marginBottom: itemMarginBottom,
    },
    pubHeaderRow: {
      flexDirection: "row",
      alignItems: "flex-start",
    },
    pubNumber: {
      fontFamily: boldFont,
      fontSize: 8.5,
      color: "#0f172a",
      width: 22,
      marginRight: 4,
      flexShrink: 0,
    },
    pubContent: {
      flex: 1,
    },
    citationText: {
      fontSize: 9,
      lineHeight: 1.4,
      color: "#0f172a",
      marginBottom: 2,
    },
    researchAreaItem: {
      marginBottom: 6,
    },
    researchAreaTitle: {
      fontFamily: boldFont,
      fontSize: 9.5,
      color: "#0f172a",
      marginBottom: 2,
    },
    researchAreaDescription: {
      fontFamily: regularFont,
      fontSize: 9,
      lineHeight: 1.4,
      color: "#334155",
      marginBottom: 2,
    },
    researchAreaKeywords: {
      fontFamily: italicFont,
      fontSize: 8,
      color: "#64748b",
    },
    footer: {
      position: "absolute",
      bottom: 20,
      left: 40,
      right: 40,
      flexDirection: "row",
      justifyContent: "space-between",
      fontSize: 8,
      color: "#64748b",
      borderTopWidth: 0.5,
      borderTopColor: "#cbd5e1",
      paddingTop: 5,
    },
  });
}
